import React, { FC, useContext } from 'react';
import { defineMessages, T } from '@sm/intl';
import { StaticContext } from '@sm/webassets';
import { SurveyLink } from '../SurveyLink';
import useStyles from './useStyles';

const DEFAULT_COPY = defineMessages({
  PRIVACY: {
    id: 'FooterPrivacyAndCookieNotice.Privacy',
    defaultMessage: 'Privacy',
    description: '[Type: Link][Vis: high] - Privacy anchor element',
  },
  COOKIE_NOTICE: {
    id: 'FooterPrivacyAndCookieNotice.CookieNotice',
    defaultMessage: 'Cookie Notice',
    description: '[Type: Link][Vis: high] - Cookie notice anchor element',
  },
  REPORT: {
    id: 'FooterPrivacyAndCookieNotice.Report',
    defaultMessage: 'Report a problem with this survey',
    description: '[Type: Link][Vis: high] - Report a problem anchor element',
  },
});

export type FooterPrivacyAndCookieNoticeProps = {
  /** Boolean to determine if we show the 'report a problem with this survey' anchor element */
  showReportProblem: boolean;
  /** URL for the report problem anchor element */
  reportProblemURL: string;
  /** Boolean to determine if we show the page request ID or not */
  showRequestId: boolean;
  /** Request Id for REX router debugging */
  pageRequestId: string;
};

const FooterPrivacyAndCookieNotice: FC<FooterPrivacyAndCookieNoticeProps> = ({
  reportProblemURL,
  showReportProblem,
  pageRequestId,
  showRequestId,
}) => {
  const {
    environment: { domain, subdomain, tld },
  } = useContext(StaticContext);

  const CURRENT_DOMAIN_URL = `https://${subdomain}.${domain}.${tld}`;
  const COOKIE_NOTICE_URL = `${CURRENT_DOMAIN_URL}/mp/legal/cookies/?ut_source=survey_pp`;
  const PRIVACY_BASICS_URL = `${CURRENT_DOMAIN_URL}/mp/legal/privacy-basics/?ut_source=survey_pp`;

  const { text, routerDebugInfo } = useStyles();
  return (
    <p className={text}>
      <SurveyLink
        rel="noreferrer"
        target="_blank"
        href={PRIVACY_BASICS_URL}
        data-testid="FooterPrivacyAndCookieNotice__Privacy"
      >
        <T desc={DEFAULT_COPY.PRIVACY} />
      </SurveyLink>{' '}
      &{' '}
      <SurveyLink
        rel="noreferrer"
        target="_blank"
        href={COOKIE_NOTICE_URL}
        data-testid="FooterPrivacyAndCookieNotice__Cookie"
      >
        <T desc={DEFAULT_COPY.COOKIE_NOTICE} />
      </SurveyLink>
      {showRequestId && (
        <span className={routerDebugInfo} aria-hidden="true">
          respweb:{pageRequestId}
        </span>
      )}
      {showReportProblem && (
        <>
          <span aria-hidden>&nbsp;&nbsp;|&nbsp;&nbsp;</span>
          <SurveyLink href={reportProblemURL}>
            <T desc={DEFAULT_COPY.REPORT} />
          </SurveyLink>
        </>
      )}
    </p>
  );
};

export default FooterPrivacyAndCookieNotice;
